import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { Landmark, Loader2, ShieldAlert, Save, RefreshCw, Download, CheckCircle, UserSearch } from 'lucide-react';
import FraudProfileModal from './FraudProfileModal';

const STATUS_STYLE = {
  settled: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  held: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-rose-50 text-rose-600 border-rose-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
};

const RULE_LABELS = {
  very_large_amount: 'Very large amount (≥$1,000)',
  large_amount: 'Large amount (≥$500)',
  rapid_repeat_payments: 'Rapid repeats (3+ / 10 min)',
  payment_burst: 'Payment burst (5+ / hour)',
  new_device: 'New device fingerprint',
  amount_far_above_user_average: 'Far above user average',
  wallet_frozen: 'Frozen wallet',
  new_wallet: 'New wallet (<24h)',
};

export default function SettlementsPanel() {
  const [data, setData] = useState({ settlements: [], summary: {} });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, cfg] = await Promise.all([
        apiCall('/api/admin/finance/settlements?days=90'),
        apiCall('/api/admin/finance/settlement-settings'),
      ]);
      setData(s);
      setSettings(cfg.settings);
    } catch (e) {
      toast.error('Could not load settlements', { description: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const [reseeding, setReseeding] = useState(false);

  const reseedDemo = async () => {
    setReseeding(true);
    try {
      const r = await apiCall('/api/admin/demo/reseed-payroll', { method: 'POST' });
      toast.success('Demo shifts reseeded', {
        description: `${r.seeded} fresh shifts (${r.pending} pending to pay, ${r.paid} already paid) — ${r.removed} old ones cleared`,
      });
      load();
    } catch (e) { toast.error('Reseed failed', { description: e.message }); }
    setReseeding(false);
  };

  const act = async (id, action) => {
    try {
      const res = await apiCall(`/api/admin/finance/settlements/${id}/${action}`, { method: 'POST' });
      res.success ? toast.success(`Settlement ${action}d`) : toast.error(res.error || `Could not ${action}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await apiCall('/api/admin/finance/settlement-settings', {
        method: 'PUT',
        body: JSON.stringify({
          mode: settings.mode,
          fraud_flag_threshold: parseInt(settings.fraud_flag_threshold, 10),
          fraud_hold_threshold: parseInt(settings.fraud_hold_threshold, 10),
          auto_hold: !!settings.auto_hold,
          rule_weights: Object.fromEntries(
            Object.entries(settings.rule_weights || {}).map(([k, v]) => [k, parseInt(v, 10)])
          ),
        }),
      });
      setSettings(res.settings);
      toast.success('Settlement settings saved');
    } catch (e) { toast.error('Save failed', { description: e.message }); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  const sum = data.summary || {};
  const rows = data.settlements || [];
  const held = rows.filter((r) => r.status === 'held');
  const failed = rows.filter((r) => r.status === 'failed');
  const scheduled = rows.filter((r) => r.status === 'scheduled');

  return (
    <div className="space-y-4" data-testid="settlements-panel">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Landmark className="w-5 h-5 text-indigo-600" /> Settlement Engine</h2>
          <p className="text-sm text-slate-500">Every paid Stripe session is validated, fraud-screened and allocated here</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reseedDemo} disabled={reseeding}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="reseed-demo-btn">
            {reseeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reseed demo shifts
          </button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/finance/settlements/report.csv?days=90`}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
            data-testid="settlements-csv-btn">
            <Download className="w-3.5 h-3.5" /> CSV
          </a>
          <button onClick={load} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {['settled', 'scheduled', 'held', 'failed', 'processing'].map((st) => (
          <div key={st} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`settlement-stat-${st}`}>
            <p className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border inline-block ${STATUS_STYLE[st]}`}>{st}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{sum[st]?.count || 0}</p>
            <p className="text-xs text-slate-500">${(sum[st]?.value || 0).toFixed(2)} total</p>
          </div>
        ))}
      </div>

      {settings && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="settlement-settings-card">
          <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-500" /> Settlement & fraud rules</h3>
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Mode</label>
              <select value={settings.mode} onChange={(e) => setSettings((p) => ({ ...p, mode: e.target.value }))}
                className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm" data-testid="settlement-mode-select">
                <option value="instant">Instant (auto)</option>
                <option value="manual">Manual approval</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Flag score ≥</label>
              <input type="number" min="0" max="100" value={settings.fraud_flag_threshold}
                onChange={(e) => setSettings((p) => ({ ...p, fraud_flag_threshold: e.target.value }))}
                className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm" data-testid="fraud-flag-input" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Hold score ≥</label>
              <input type="number" min="0" max="100" value={settings.fraud_hold_threshold}
                onChange={(e) => setSettings((p) => ({ ...p, fraud_hold_threshold: e.target.value }))}
                className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm" data-testid="fraud-hold-input" />
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!settings.auto_hold}
                  onChange={(e) => setSettings((p) => ({ ...p, auto_hold: e.target.checked }))}
                  className="w-4 h-4 accent-amber-500" data-testid="auto-hold-toggle" />
                Auto-hold risky
              </label>
              <button onClick={saveSettings} disabled={saving}
                className="ml-auto px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="save-settlement-settings">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100">
            <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-2">Fraud rule weights <span className="normal-case font-normal text-slate-400">— points added per rule (0 disables)</span></h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-3" data-testid="fraud-rule-weights">
              {Object.entries(RULE_LABELS).map(([rule, label]) => (
                <div key={rule}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[11px] text-slate-600">{label}</span>
                    <span className="text-[11px] font-bold text-indigo-600" data-testid={`rule-weight-value-${rule}`}>{settings.rule_weights?.[rule] ?? 0}</span>
                  </div>
                  <input type="range" min="0" max="100" step="5"
                    value={settings.rule_weights?.[rule] ?? 0}
                    onChange={(e) => setSettings((p) => ({ ...p, rule_weights: { ...p.rule_weights, [rule]: parseInt(e.target.value, 10) } }))}
                    className="w-full accent-indigo-600 h-1.5"
                    data-testid={`rule-weight-slider-${rule}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {(held.length > 0 || failed.length > 0 || scheduled.length > 0) && (
        <div className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-amber-100 bg-amber-50">
            <h3 className="font-bold text-amber-800 text-sm">Needs attention ({held.length + failed.length + scheduled.length})</h3>
          </div>
          <div className="divide-y divide-slate-100" data-testid="settlements-attention-list">
            {[...held, ...failed, ...scheduled].map((s) => (
              <div key={s._id} className="px-4 py-3 flex items-center gap-3 flex-wrap text-sm">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${STATUS_STYLE[s.status]}`}>{s.status}</span>
                <span className="font-semibold text-slate-900">${Number(s.amount).toFixed(2)}</span>
                <span className="text-xs text-slate-500 flex-1 min-w-[140px]">{s.source} · risk {s.fraud_score ?? 0}{s.hold_reason ? ` · ${s.hold_reason}` : ''}{s.error ? ` · ${s.error.slice(0, 60)}` : ''}{s.scheduled_for ? ` · due ${new Date(s.scheduled_for).toLocaleString()}` : ''}</span>
                {s.status === 'held' && (
                  <button onClick={() => act(s._id, 'approve')} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1" data-testid={`approve-settlement-${s._id}`}>
                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                  </button>
                )}
                {s.status === 'failed' && (
                  <button onClick={() => act(s._id, 'retry')} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold" data-testid={`retry-settlement-${s._id}`}>Retry</button>
                )}
                {s.status === 'scheduled' && (
                  <button onClick={() => act(s._id, 'release')} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold" data-testid={`release-settlement-${s._id}`}>Release now</button>
                )}
                {s.payer_id && (
                  <button onClick={() => setProfileId(s.payer_id)} title="Payer fraud profile"
                    className="p-1.5 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50"
                    data-testid={`fraud-profile-attn-btn-${s._id}`}>
                    <UserSearch className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <h3 className="font-bold text-slate-900 text-sm">Recent settlements</h3>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8" data-testid="no-settlements">No settlements yet — they appear when Stripe payments complete</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="settlements-list">
            {rows.slice(0, 25).map((s) => (
              <div key={s._id} className="px-4 py-2.5 flex items-center gap-3 text-sm flex-wrap">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${STATUS_STYLE[s.status] || ''}`}>{s.status}</span>
                <span className="font-semibold text-slate-900">${Number(s.amount).toFixed(2)}</span>
                <span className="text-xs text-slate-500">{s.source}</span>
                <span className="text-xs text-slate-400 flex-1 text-right">risk {s.fraud_score ?? 0} · {s.method} · {new Date(s.created_at).toLocaleString()}</span>
                {s.payer_id && (
                  <button onClick={() => setProfileId(s.payer_id)} title="Payer fraud profile"
                    className="p-1.5 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50"
                    data-testid={`fraud-profile-btn-${s._id}`}>
                    <UserSearch className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {profileId && <FraudProfileModal payerId={profileId} onClose={() => setProfileId(null)} />}
    </div>
  );
}
